/** @jsxImportSource @emotion/react */
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { WEDDING_GIFT_CONFIRMATION_URL } from "../../constants";
import { LocaleContext } from "../../context/LocaleContext";
import { formatTimestamp } from "../../helpers/formattedTimestamp";
import {
  normalizeDataWishes,
  WishesData,
} from "../../helpers/wishesNormalizer";
import Button from "../Button";
import { styMargin, stySectionTitle } from "../styles";
import {
  txtInputNamePlaceholder,
  txtInputPeoplePlaceholder,
  txtInputWishesPlaceHolder,
  txtRSVPTitle,
  txtSelectRSVPNotAttend,
  txtSelectRSVPNotSure,
  txtSelectRSVPPlaceholder,
  txtSelectRSVPSure,
  txtSendRSVP,
  txtSendingRSVP,
  txtSentRSVP,
} from "./locales";
import {
  styFormContainer,
  styWishesCard,
  styWishListConatiner,
} from "./styles";

type AttendanceOption =
  | "Hadir!"
  | "Masih ragu-ragu"
  | "Maaf, belum bisa hadir!"
  | "";

type RSVPFormData = {
  name: string;
  attendance: AttendanceOption;
  totalPeople: string;
  wishes: string;
};

const RSVPSection = () => {
  const { lang } = useContext(LocaleContext);
  const [formData, setFormData] = useState<RSVPFormData>({
    name: "",
    attendance: "",
    totalPeople: "",
    wishes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [wishList, setWishList] = useState<WishesData[]>([]);

  const isCalledOnce = useRef(false);
  const isConfirmOnce = useRef(false);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSelectAttendance = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      attendance: value as AttendanceOption,
    }));
  };

  const handleSelectPeople = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      totalPeople: value,
    }));
  };

  const handleGetWishesData = useCallback(async () => {
    try {
      const response = await fetch(WEDDING_GIFT_CONFIRMATION_URL, {
        method: "GET",
      });

      if (response.ok) {
        const data = await response.text();
        const dataParse = JSON.parse(data);
        const dataResponse = dataParse.data;
        const normalizeData = normalizeDataWishes(dataResponse);
        setWishList(normalizeData);
      } else {
        console.error("Failed to get data");
      }
    } catch (error) {
      console.error("Failed to get data:", error);
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const newData = new FormData();
    newData.append("Name", formData.name);
    newData.append("Attendance", formData.attendance);
    newData.append("People", String(formData.totalPeople));
    newData.append("Wishes", formData.wishes);
    newData.append("Time", String(new Date()));

    try {
      setSubmitting(true);
      const response = await fetch(WEDDING_GIFT_CONFIRMATION_URL, {
        method: "POST",
        body: newData,
      });

      if (response.ok) {
        console.log("Data inserted successfully");
        isConfirmOnce.current = true;
        handleGetWishesData();
      } else {
        console.error("Failed to insert data");
      }
    } catch (error) {
      console.error("Error inserting data:", error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isCalledOnce.current) {
      isCalledOnce.current = true;
      handleGetWishesData();
    }
  }, [handleGetWishesData]);

  return (
    <div>
      <div css={stySectionTitle}>
        <h1>{txtRSVPTitle[lang]}</h1>
      </div>
      <form onSubmit={handleSubmit} css={styFormContainer}>
        <div css={styMargin("12px 0")}>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder={txtInputNamePlaceholder[lang]}
          />
        </div>

        <div css={styMargin("12px 0")}>
          <select
            name="attendance"
            value={formData.attendance}
            onChange={handleSelectAttendance}
          >
            <option value="" disabled>
              {txtSelectRSVPPlaceholder[lang]}
            </option>
            <option value="Hadir!">{txtSelectRSVPSure[lang]}</option>
            <option value="ragu-ragu">{txtSelectRSVPNotSure[lang]}</option>
            <option value="tidak hadir">{txtSelectRSVPNotAttend[lang]}</option>
          </select>
        </div>

        {formData.attendance === "Hadir!" && (
          <div css={styMargin("12px 0")}>
            <input
              type="number"
              name="totalPeople"
              value={formData.totalPeople}
              onChange={handleSelectPeople}
              placeholder={txtInputPeoplePlaceholder[lang]}
            />
          </div>
        )}

        <div css={styMargin("12px 0")}>
          <textarea
            name="wishes"
            value={formData.wishes}
            onChange={handleInputChange}
            placeholder={txtInputWishesPlaceHolder[lang]}
            maxLength={250}
          />
        </div>

        <Button
          type="submit"
          disabled={
            submitting ||
            (formData.attendance === "Hadir!" && !formData.totalPeople)
          }
        >
          {submitting
            ? txtSendingRSVP[lang]
            : isConfirmOnce.current
            ? txtSentRSVP[lang]
            : txtSendRSVP[lang]}
        </Button>
      </form>

      <div css={styWishListConatiner}>
        {wishList.map((item, idx) => {
          if (!item.Wishes) return null;
          return (
            <div css={styWishesCard} key={idx}>
              <p className="name">{item.Name}</p>
              <p className="message">{item.Wishes}</p>
              <p className="timestamp">
                {formatTimestamp(new Date(item.Time), lang)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RSVPSection;
